import { useState } from 'react'
import { useCollections, useCollectionsSummary } from '@hooks/useAnalytics'

export function CollectionsPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('pending')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useCollections(search, status)
  const { data: summaryData } = useCollectionsSummary()

  const collections = data?.data?.items || []
  const total = data?.data?.total || 0
  const pageSize = data?.data?.pageSize || 20
  const totalPages = data?.data?.totalPages || 0
  const summary = summaryData?.data

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Collections Management</h1>
        <p className="text-gray-600 text-sm mt-1">
          Track outstanding invoices and payment collection status
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-blue-500">
            <div className="text-xs text-blue-600 font-semibold uppercase">
              Total Due
            </div>
            <div className="text-2xl font-bold text-blue-900 mt-1">
              ₹{(summary.totalDue || 0).toLocaleString()}
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-l-4 border-green-500">
            <div className="text-xs text-green-600 font-semibold uppercase">
              Collected
            </div>
            <div className="text-2xl font-bold text-green-900 mt-1">
              ₹{(summary.totalPaid || 0).toLocaleString()}
            </div>
          </div>

          <div className="card bg-gradient-to-br from-orange-50 to-orange-100 border-l-4 border-orange-500">
            <div className="text-xs text-orange-600 font-semibold uppercase">
              Outstanding
            </div>
            <div className="text-2xl font-bold text-orange-900 mt-1">
              ₹{(summary.totalOutstanding || 0).toLocaleString()}
            </div>
          </div>

          <div className="card bg-gradient-to-br from-red-50 to-red-100 border-l-4 border-red-500">
            <div className="text-xs text-red-600 font-semibold uppercase">
              Pending
            </div>
            <div className="text-2xl font-bold text-red-900 mt-1">
              {summary.pendingCount || 0}
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-l-4 border-purple-500">
            <div className="text-xs text-purple-600 font-semibold uppercase">
              Collection Rate
            </div>
            <div className="text-2xl font-bold text-purple-900 mt-1">
              {(summary.collectionRate || 0).toFixed(1)}%
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="space-y-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Search by store or invoice..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full"
            />

            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              className="w-full"
            >
              <option value="pending">Pending Collections</option>
              <option value="">All Ledgers</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            Failed to load collections. Please try again.
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : collections.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {search ? 'No collections found.' : 'No pending collections.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-2 font-semibold">Store</th>
                    <th className="text-left px-4 py-2 font-semibold">Invoice</th>
                    <th className="text-right px-4 py-2 font-semibold">Amount Due</th>
                    <th className="text-right px-4 py-2 font-semibold">Paid</th>
                    <th className="text-right px-4 py-2 font-semibold">Outstanding</th>
                    <th className="text-center px-4 py-2 font-semibold">Collection %</th>
                  </tr>
                </thead>
                <tbody>
                  {collections.map((collection) => {
                    const collectionPercent =
                      collection.amountDue > 0
                        ? (collection.amountPaid / collection.amountDue) * 100
                        : 0
                    return (
                      <tr
                        key={collection.id}
                        className="border-b hover:bg-gray-50 transition"
                      >
                        <td className="px-4 py-3 font-medium">
                          {collection.store.storeName}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {collection.invoice?.invoiceNo || '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          ₹{collection.amountDue.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600 font-medium">
                          ₹{collection.amountPaid.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          <span
                            className={
                              collection.outstandingAmount > 0
                                ? 'text-red-600'
                                : 'text-green-600'
                            }
                          >
                            ₹{collection.outstandingAmount.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="w-12 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
                            <div
                              className="h-full bg-green-500 transition-all"
                              style={{ width: `${Math.min(collectionPercent, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-600 mt-1 block">
                            {collectionPercent.toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Showing {(page - 1) * pageSize + 1} to{' '}
                  {Math.min(page * pageSize, total)} of {total}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-2 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="btn-secondary disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
